import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, CircularProgress } from '@mui/material';
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table';
import moment from 'moment';

import { getClientSignal } from '../../../../api';
import { CopyButton } from '../../../../components/CopyButton';
import { TableGridLocal } from '../../../../components/TableGridLocal';
import { ClientSignal } from '../../../../types/clientSignal';
import { ExecutionPortfolioPosition } from '../../../../types/executionPortfolio';
import { ExecutionPosition } from '../../../../types/executionPosition';

const parseJson = <T,>(value: string, name: string): T[] => {
  try {
    const obj = JSON.parse(value);
    const data = obj[name];
    return data && Array.isArray(data) ? (data as T[]) : [];
  } catch (e) {
    console.error(` *** incorrect signal value message=${(e as Error).toString()}`);
    return [];
  }
};

export const SignalTable: React.FC<{ agreementId: string }> = ({ agreementId }) => {
  const [signals, setSignals] = useState<ClientSignal[]>([]);
  const [isFullHistory, setIsFullHistory] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadSignals = useCallback(
    async (startDate?: string) => {
      setIsLoading(true);

      try {
        const response = await getClientSignal({ agreementId, startDate });
        if (response.success) {
          setSignals(response.result ?? []);
          setIsFullHistory(!startDate);
        }
      } catch (e) {
        console.error(` *** getClientSignal message=${(e as Error).toString()}`);
      } finally {
        setIsLoading(false);
      }
    },
    [agreementId],
  );

  useEffect(() => {
    loadSignals(moment().subtract(1, 'month').format('YYYY-MM-DD'));
  }, [loadSignals]);

  const columns = useMemo<MRT_ColumnDef<ClientSignal>[]>(
    () => [
      {
        accessorKey: 'id' as string,
        header: 'Id',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'signalId',
        header: 'SignalId',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'dateTime',
        header: 'Дата и время',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'type',
        header: 'type',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'processedType' as string,
        header: 'Processed',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'realizedPnL',
        header: 'RealizedPnL',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'rPLFunds' as string,
        header: 'rPL Funds',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'message' as string,
        header: 'Message',
        enableColumnFilter: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'execution',
        header: 'Execution',
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const execution = cell.row.original.execution;
          if (!execution || typeof execution !== 'string') return null;

          const positions = parseJson<ExecutionPosition>(execution, 'Positions');
          const preview = positions
            .slice(0, 3)
            .map((p, i) => <div key={i}>{`${p.Symbol} ${p.ClassCode} ${p.ExecNumber} × ${p.ExecPrice}`}</div>);

          return (
            <>
              {positions.length > 0 && preview}
              {positions.length > 2 && <div>...</div>}
              <CopyButton text={execution} />
            </>
          );
        },
      },
      {
        accessorKey: 'portfolio',
        header: 'Portfolio',
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const portfolioStr = cell.row.original.portfolio;
          if (!portfolioStr || typeof portfolioStr !== 'string') return null;

          const portfolio = parseJson<ExecutionPortfolioPosition>(portfolioStr, 'Positions');
          const preview = portfolio.slice(0, 3).map((p, i) => {
            const isMoney = !p.SecurityKey || p.SecurityKey.length === 0;
            return (
              <div key={i}>
                {isMoney
                  ? `Деньги ${p.Number} ${p.Currency}`
                  : `${p.SecurityKey} ${p.AvgPrice} x ${p.Number} ${p.Currency}`}
              </div>
            );
          });

          return (
            <>
              {portfolio.length > 0 && preview}
              {portfolio.length > 2 && <div>...</div>}
              <CopyButton text={portfolioStr} />
            </>
          );
        },
      },
      {
        accessorKey: 'archived',
        header: 'Archived',
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ cell }) => <>{cell.row.original.archived ? 'Да' : 'Нет'}</>,
      },
    ],
    [],
  );

  const columnSorts = useMemo<MRT_SortingState>(
    () => [
      {
        desc: true,
        id: 'dateTime',
      },
    ],
    [],
  );

  return (
    <Box sx={{ mt: 2 }}>
      <TableGridLocal
        name="statistic-signal-table"
        columns={columns}
        data={signals}
        pageSize={50}
        columnSorts={columnSorts}
        totalRowCount={signals.length}
        bottomToolbarCustomActions={
          !isFullHistory ? (
            <Button
              variant="contained"
              onClick={() => loadSignals()}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {isLoading ? 'Загрузка…' : 'Далее'}
            </Button>
          ) : undefined
        }
      />
    </Box>
  );
};
